import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import AuthCallback from "@/pages/AuthCallback";
import LoginPage from "@/pages/LoginPage";
import HomePage from "@/pages/HomePage";
import RestaurantDetailPage from "@/pages/RestaurantDetailPage";
import ReservationsPage from "@/pages/ReservationsPage";
import DiningSessionPage from "@/pages/DiningSessionPage";
import PaymentPage from "@/pages/PaymentPage";
import SavedCardsPage from "@/pages/SavedCardsPage";
import NotificationsPage from "@/pages/NotificationsPage";
import StaffOverviewPage from "@/pages/staff/StaffOverviewPage";
import StaffHostPage from "@/pages/staff/StaffHostPage";
import KitchenPage from "@/pages/kitchen/KitchenPage";
import AdminOverviewPage from "@/pages/admin/AdminOverviewPage";
import AdminRestaurantsPage from "@/pages/admin/AdminRestaurantsPage";
import AdminRestaurantDetailPage from "@/pages/admin/AdminRestaurantDetailPage";
import AppLayout from "@/components/layout/AppLayout";
import { Loader2 } from "lucide-react";
import { roleHomePath } from "@/lib/roles";

function ProtectedRoute({ children, roles }) {
    const { user, loading } = useAuth();
    const location = useLocation();
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
        );
    }
    if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
    if (roles && !roles.includes(user.role)) {
        return <Navigate to={roleHomePath(user.role)} replace />;
    }
    return children;
}

function AppRouter() {
    // Detect session_id during render to avoid race conditions
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
        return <AuthCallback />;
    }
    return (
        <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route element={<ProtectedRoute roles={["customer", "admin"]}><AppLayout /></ProtectedRoute>}>
                <Route path="/" element={<HomePage />} />
                <Route path="/restaurants/:id" element={<RestaurantDetailPage />} />
                <Route path="/reservations" element={<ReservationsPage />} />
                <Route path="/dining/:reservationId" element={<DiningSessionPage />} />
                <Route path="/payment/:reservationId" element={<PaymentPage />} />
                <Route path="/cards" element={<SavedCardsPage />} />
                <Route path="/notifications" element={<NotificationsPage />} />
            </Route>
            <Route element={<ProtectedRoute roles={["staff", "admin"]}><AppLayout /></ProtectedRoute>}>
                <Route path="/staff" element={<StaffOverviewPage />} />
                <Route path="/staff/host" element={<StaffHostPage />} />
            </Route>
            <Route element={<ProtectedRoute roles={["kitchen", "admin"]}><AppLayout /></ProtectedRoute>}>
                <Route path="/kitchen" element={<KitchenPage />} />
            </Route>
            <Route element={<ProtectedRoute roles={["admin"]}><AppLayout /></ProtectedRoute>}>
                <Route path="/admin" element={<AdminOverviewPage />} />
                <Route path="/admin/restaurants" element={<AdminRestaurantsPage />} />
                <Route path="/admin/restaurants/:restaurantId" element={<AdminRestaurantDetailPage />} />
                <Route path="/admin/restaurants/:restaurantId/:section" element={<AdminRestaurantDetailPage />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

function App() {
    return (
        <BrowserRouter>
            <AuthProvider>
                <AppRouter />
                <Toaster position="top-right" richColors />
            </AuthProvider>
        </BrowserRouter>
    );
}

export default App;
